import { useCallback, useEffect, useState } from "react";
import { Gauge, Plus, Trash2 } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";

const Bar = ({ label, value, target, testid }) => (
    <div data-testid={testid}>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-ash">
            <span>{label}</span>
            <span className="text-bone">{value == null ? "—" : `${value}%`}</span>
        </div>
        <div className="relative mt-1.5 h-1.5 bg-white/10">
            <div
                className="absolute inset-y-0 left-0 bg-gold transition-all duration-700"
                style={{ width: `${Math.min(100, value || 0)}%` }}
            />
            {target != null ? (
                <div
                    className="absolute -inset-y-1 w-px bg-bone/70"
                    style={{ left: `${target}%` }}
                    title={`Doel ${target}%`}
                />
            ) : null}
        </div>
    </div>
);

const EMPTY = { machine: "", date: "", planned_minutes: "", downtime_minutes: "0", expected_units: "", produced_units: "", waste_units: "0" };

export const OeeTab = () => {
    const [data, setData] = useState(null);
    const [logs, setLogs] = useState([]);
    const [machines, setMachines] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [o, l, m] = await Promise.all([
                platformClient.get("/production/oee?days=30"),
                platformClient.get("/production/machine-logs?days=30"),
                platformClient.get("/production/machines"),
            ]);
            setData(o.data);
            setLogs(l.data);
            setMachines(m.data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/production/machine-logs", {
                machine: form.machine,
                date: form.date,
                planned_minutes: Number(form.planned_minutes),
                downtime_minutes: Number(form.downtime_minutes) || 0,
                expected_units: Number(form.expected_units),
                produced_units: Number(form.produced_units) || 0,
                waste_units: Number(form.waste_units) || 0,
            });
            setForm(EMPTY);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (log) => {
        try {
            await platformClient.delete(`/production/machine-logs/${log.id}`);
            load();
        } catch {}
    };

    const overall = data?.overall;

    return (
        <div data-testid="oee-tab" className="mt-8">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="border border-gold/30 bg-coal p-6">
                    <p className={labelCls}>
                        <Gauge className="mr-2 inline h-3.5 w-3.5 text-gold" />
                        OEE — laatste 30 dagen
                    </p>
                    <p data-testid="oee-overall" className="mt-3 font-display text-5xl tracking-tight text-gold">
                        {overall?.oee != null ? `${overall.oee}%` : "—"}
                    </p>
                    <p className="mt-2 text-xs text-ash">
                        Doel: <span className="text-bone">{data?.target ?? 85}%</span>
                        {overall?.oee != null ? (
                            <span className={overall.oee >= (data?.target ?? 85) ? " text-emerald-400" : " text-red-300"}>
                                {" "}· {overall.oee >= (data?.target ?? 85) ? "op of boven doel" : "onder doel"}
                            </span>
                        ) : null}
                    </p>
                    {overall ? (
                        <div className="mt-5 space-y-3">
                            <Bar label="Beschikbaarheid" value={overall.availability} testid="oee-bar-availability" />
                            <Bar label="Prestatie" value={overall.performance} testid="oee-bar-performance" />
                            <Bar label="Kwaliteit" value={overall.quality} testid="oee-bar-quality" />
                        </div>
                    ) : (
                        <p className="mt-5 text-xs text-ash">Nog geen registraties — voer hiernaast je eerste productiedag in.</p>
                    )}
                </div>

                <div className="border border-white/10 bg-coal p-6 lg:col-span-2">
                    <p className={labelCls}>Dagregistratie per lijn</p>
                    <form data-testid="oee-form" onSubmit={submit} className="mt-4 grid grid-cols-2 gap-5 sm:grid-cols-3">
                        <label className="block">
                            <span className={labelCls}>Machine / lijn</span>
                            <select
                                data-testid="oee-form-machine"
                                value={form.machine}
                                onChange={(e) => setForm({ ...form, machine: e.target.value })}
                                required
                                className="mt-2 w-full border-b border-white/20 bg-ink pb-2.5 text-sm text-bone focus:border-gold focus:outline-none"
                            >
                                <option value="">— kies —</option>
                                {machines.map((m) => (
                                    <option key={m.id} value={m.name}>{m.name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className={labelCls}>Datum</span>
                            <input
                                data-testid="oee-form-date"
                                type="date"
                                value={form.date}
                                onChange={(e) => setForm({ ...form, date: e.target.value })}
                                required
                                className={`${inputCls} [color-scheme:dark]`}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Geplande tijd (min)</span>
                            <input
                                data-testid="oee-form-planned"
                                type="number" min="1" max="1440" required
                                value={form.planned_minutes}
                                onChange={(e) => setForm({ ...form, planned_minutes: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Stilstand (min)</span>
                            <input
                                data-testid="oee-form-downtime"
                                type="number" min="0" max="1440"
                                value={form.downtime_minutes}
                                onChange={(e) => setForm({ ...form, downtime_minutes: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Verwachte output</span>
                            <input
                                data-testid="oee-form-expected"
                                type="number" min="1" step="any" required
                                value={form.expected_units}
                                onChange={(e) => setForm({ ...form, expected_units: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Geproduceerd</span>
                            <input
                                data-testid="oee-form-produced"
                                type="number" min="0" step="any"
                                value={form.produced_units}
                                onChange={(e) => setForm({ ...form, produced_units: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Uitval / afkeur</span>
                            <input
                                data-testid="oee-form-waste"
                                type="number" min="0" step="any"
                                value={form.waste_units}
                                onChange={(e) => setForm({ ...form, waste_units: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                        <div className="col-span-2 flex items-end sm:col-span-1">
                            <button
                                data-testid="oee-form-submit"
                                type="submit"
                                disabled={busy}
                                className="flex w-full items-center justify-center gap-2 bg-gold px-5 py-3 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone disabled:opacity-60"
                            >
                                <Plus className="h-4 w-4" /> {busy ? "Opslaan…" : "Registreer"}
                            </button>
                        </div>
                    </form>
                    {error ? (
                        <p data-testid="oee-form-error" className="mt-4 text-sm text-red-400">{error}</p>
                    ) : null}
                    <p className="mt-4 text-[11px] text-ash/70">
                        Zelfde lijn + datum opnieuw invoeren = bestaande registratie bijwerken.
                    </p>
                </div>
            </div>

            {data?.lines?.length ? (
                <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {data.lines.map((l) => (
                        <div key={l.machine} data-testid={`oee-line-${l.machine}`} className="border border-white/10 bg-coal p-6">
                            <div className="flex items-baseline justify-between">
                                <p className="text-sm text-bone">{l.machine}</p>
                                <p className={`font-display text-2xl ${l.oee != null && l.oee >= data.target ? "text-gold" : "text-bone"}`}>
                                    {l.oee != null ? `${l.oee}%` : "—"}
                                </p>
                            </div>
                            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ash">
                                {l.days_logged} dag(en) geregistreerd
                            </p>
                            <div className="mt-4 space-y-3">
                                <Bar label="Beschikbaarheid" value={l.availability} />
                                <Bar label="Prestatie" value={l.performance} />
                                <Bar label="Kwaliteit" value={l.quality} />
                                <Bar label="OEE" value={l.oee} target={data.target} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}

            {logs.length ? (
                <div className="mt-8 border border-white/10 bg-coal p-6">
                    <p className={labelCls}>Recente registraties</p>
                    <div className="mt-4 space-y-2">
                        {logs.slice(0, 12).map((l) => (
                            <div
                                key={l.id}
                                data-testid={`oee-log-${l.id}`}
                                className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 pb-2 text-xs"
                            >
                                <span className="text-bone">
                                    <span className="text-gold">{l.date}</span> · {l.machine}
                                </span>
                                <span className="text-ash">
                                    {l.produced_units}/{l.expected_units} stuks · {l.downtime_minutes} min stilstand · {l.waste_units} uitval
                                </span>
                                <button
                                    data-testid={`oee-log-delete-${l.id}`}
                                    onClick={() => remove(l)}
                                    aria-label="Registratie verwijderen"
                                    className="text-ash/50 transition-colors hover:text-red-400"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};
